<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">medication</span>
      <p>
        Administra el catálogo de interacciones medicamentosas: pares de
        medicamentos que pueden causar efectos adversos al administrarse
        juntos, con su nivel de severidad y recomendación clínica.
      </p>
    </div>

    <!-- Filtros -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">filter_list</span>
        Filtros de búsqueda
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in filtros" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Severidad -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">warning</span>
        Niveles de severidad
      </h3>
      <div class="help-estados">
        <span class="help-estado-badge" style="background:#f9e79f;color:#7d6608">LEVE</span>
        <span class="help-estado-badge" style="background:#f8c471;color:#784212">MODERADA</span>
        <span class="help-estado-badge" style="background:#ec7063;color:#fff">GRAVE</span>
      </div>
      <div class="help-cards" style="margin-top:0.5rem">
        <div class="help-card">
          <span class="material-symbols-outlined help-card-icon" style="color:#b7950b">warning</span>
          <div>
            <p class="help-card-title">Leve</p>
            <p class="help-card-desc">Interacción de bajo riesgo. Puede requerir monitorización pero generalmente no requiere cambio de tratamiento.</p>
          </div>
        </div>
        <div class="help-card">
          <span class="material-symbols-outlined help-card-icon" style="color:#e67e22">error</span>
          <div>
            <p class="help-card-title">Moderada</p>
            <p class="help-card-desc">Interacción con potencial para causar efectos adversos significativos. Puede requerir ajuste de dosis o sustitución.</p>
          </div>
        </div>
        <div class="help-card">
          <span class="material-symbols-outlined help-card-icon" style="color:#c0392b">dangerous</span>
          <div>
            <p class="help-card-title">Grave</p>
            <p class="help-card-desc">Interacción potencialmente peligrosa. La combinación generalmente debe evitarse. Requiere atención médica inmediata si se administran juntos.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Campos del formulario -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">edit_note</span>
        Campos del formulario
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in campos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Estados -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">toggle_on</span>
        Estados
      </h3>
      <div class="help-estados">
        <span class="help-estado-badge" style="background:#7dcea0;color:#145a32">ACTIVO</span>
        <span class="help-estado-badge" style="background:#d5d8dc;color:#424949">INACTIVO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-top:0.5rem">
        Solo las interacciones <strong>Activas</strong> se consideran en las
        validaciones del sistema al prescribir medicamentos. Al eliminar un
        registro, este pasa a estado <strong>Inactivo</strong> en lugar de
        borrarse permanentemente.
      </p>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        El buscador de medicamentos requiere al menos <strong>2 caracteres</strong>
        para comenzar a filtrar. No puedes seleccionar el mismo medicamento
        en ambos campos: el sistema valida que sean distintos antes de guardar.
        Los filtros de la barra de búsqueda actúan sobre los datos ya cargados,
        sin necesidad de consultar el servidor nuevamente.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaInteraccionMed",
  data() {
    return {
      filtros: [
        {
          icon: "medication",
          titulo: "Medicamento",
          desc:   "Filtra por nombre del medicamento, buscando coincidencias tanto en el Medicamento 1 como en el Medicamento 2 del par registrado.",
        },
        {
          icon: "warning",
          titulo: "Severidad",
          desc:   "Filtra por nivel de severidad: Leve, Moderada o Grave. Selecciona 'Todas' para ver todas las severidades.",
        },
        {
          icon: "toggle_on",
          titulo: "Estado",
          desc:   "Por defecto muestra solo los registros Activos. Cambia a Inactivo para ver las interacciones desactivadas.",
        },
      ],
      campos: [
        {
          icon: "search",
          titulo: "Medicamento 1 y Medicamento 2",
          desc:   "Escribe al menos 2 caracteres para buscar el medicamento en el catálogo. Selecciona el resultado deseado de la lista. Ambos campos son obligatorios y deben ser medicamentos distintos.",
        },
        {
          icon: "warning",
          titulo: "Severidad",
          desc:   "Nivel de riesgo de la interacción: Leve, Moderada o Grave.",
        },
        {
          icon: "toggle_on",
          titulo: "Estado",
          desc:   "Activo: la interacción se considera en las validaciones del sistema. Inactivo: no se usa en validaciones.",
        },
        {
          icon: "text_snippet",
          titulo: "Descripción",
          desc:   "Explicación clínica del mecanismo o efecto de la interacción entre los dos medicamentos.",
        },
        {
          icon: "recommend",
          titulo: "Recomendación",
          desc:   "Indicación clínica sobre cómo actuar ante esta interacción: monitorizar, ajustar dosis, sustituir el medicamento, etc.",
        },
      ],
      acciones: [
        {
          icon:   "add_circle",
          color:  "#0059cc",
          titulo: "Nueva interacción",
          desc:   "Abre el formulario para registrar un nuevo par de medicamentos con su severidad, descripción y recomendación.",
        },
        {
          icon:   "edit",
          color:  "#198754",
          titulo: "Editar",
          desc:   "Carga los datos del registro seleccionado para modificar cualquier campo.",
        },
        {
          icon:   "delete",
          color:  "#dc3545",
          titulo: "Eliminar",
          desc:   "Solicita confirmación y pasa el registro a estado Inactivo. No elimina el dato del sistema.",
        },
        {
          icon:   "download",
          color:  "#fd7e14",
          titulo: "Exportar Excel",
          desc:   "Descarga la lista de interacciones visible (con los filtros aplicados) en formato Excel.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>